import { useState } from 'react';
import { Page, Button, Spinner, FullBox, TableList, Typography, DropdownMenu, TableListActions, useOverlayContext } from 'seagull';
import { Edit2Liner24, TrashLiner24, RefreshIcon } from 'seagull/icons';
import ExternalLinkButton from "../../components/ExternalLinkButton";
import RestfulWrapper from "../../components/RestfulWrapper/RestfulWrapper";
import { useStoreActions } from "../../contexts";
import { formatShortMonthUSDate } from "../../utils";
import { APIKeysEmptyState } from "./APIKeysEmptyState";
import { APIKeysSortField, apiKeysColumnDefs, apiKeysHeaderItems, formatAPIKeyValue } from "./APIKeysPage.utils";
import { DeactivateAPIKeyModal } from "./modals/DeactivateAPIKeyModal";
import GenerateAPIKeyModal from "./modals/GenerateAPIKeyModal";
import { RegenerateAPIKeyModal } from "./modals/RegenerateAPIKeyModal";
import { RenameAPIKeyModal } from "./modals/RenameAPIKeyModal";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const normalizeName = name => name.trim().toLowerCase();
function _getIsNameValid(name, keys, currentName) {
  const trimmedName = normalizeName(name);
  if (currentName && trimmedName === normalizeName(currentName)) {
    return true;
  }
  return !keys.find(key => normalizeName(key.name) === trimmedName);
}
export { _getIsNameValid as getIsNameValid };
const APIKeysPage = () => {
  const overlayContext = useOverlayContext();
  const {
    api
  } = useStoreActions();
  const [sort, setSort] = useState({
    field: APIKeysSortField.Name,
    direction: 'asc'
  });
  const handleGenerateAPIKey = (keys, refresh) => {
    overlayContext.showModal({
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/_jsx(GenerateAPIKeyModal, {
          getIsNameValid: name => _getIsNameValid(name, keys),
          dismissModal: dismissModal,
          refresh: refresh
        });
      },
      closeOnClickOutside: true,
      closeOnEscapeKey: true
    });
  };
  const fetchAPIKeys = () => api.business.getAPIKeys({
    sortBy: sort.field,
    sortDirection: sort.direction
  });
  const handleSortChange = tableSort => {
    if (!tableSort) {
      setSort({
        field: APIKeysSortField.Name,
        direction: 'asc'
      });
    } else {
      const fieldMap = {
        0: APIKeysSortField.Name,
        2: APIKeysSortField.CreatedOn,
        3: APIKeysSortField.UpdatedOn
      };
      setSort({
        field: fieldMap[tableSort.colIndex],
        direction: tableSort.sort
      });
    }
  };
  return /*#__PURE__*/_jsx(RestfulWrapper, {
    fetch: fetchAPIKeys,
    renderLoader: () => /*#__PURE__*/_jsx(Page, {
      header: "Anchor API keys",
      subheader: /*#__PURE__*/_jsxs(Typography, {
        children: ["Manage API keys for direct access to the", ' ', /*#__PURE__*/_jsx(ExternalLinkButton, {
          href: "https://docs.sayanchor.com",
          underline: "none",
          openInNewTab: true,
          "data-testid": "anchor-api-docs-link",
          children: "Anchor API"
        }), ' ', "or via third-party integrations"]
      }),
      "data-testid": "api-keys-page",
      children: /*#__PURE__*/_jsx(Spinner, {
        size: 24
      })
    }),
    renderError: () => /*#__PURE__*/_jsx(Page, {
      header: "Anchor API keys",
      subheader: /*#__PURE__*/_jsxs(Typography, {
        children: ["Manage API keys for direct access to the", ' ', /*#__PURE__*/_jsx(ExternalLinkButton, {
          href: "https://docs.sayanchor.com",
          underline: "none",
          openInNewTab: true,
          "data-testid": "anchor-api-docs-link",
          children: "Anchor API"
        }), ' ', "or via third-party integrations"]
      }),
      "data-testid": "api-keys-page",
      children: /*#__PURE__*/_jsx(FullBox, {
        "data-testid": "api-keys-error",
        children: /*#__PURE__*/_jsx(Typography, {
          children: "Error loading API keys. Please try again."
        })
      })
    }),
    renderChild: _ref2 => {
      let {
        data: {
          keys
        },
        refresh
      } = _ref2;
      return /*#__PURE__*/_jsx(Page, {
        header: "Anchor API keys",
        variant: "containedHeight",
        subheader: /*#__PURE__*/_jsxs(Typography, {
          children: ["Manage API keys for direct access to the", ' ', /*#__PURE__*/_jsx(ExternalLinkButton, {
            href: "https://docs.sayanchor.com",
            underline: "none",
            openInNewTab: true,
            "data-testid": "anchor-api-docs-link",
            children: "Anchor API"
          }), ' ', "or via third-party integrations"]
        }),
        actions: keys.length > 0 ? /*#__PURE__*/_jsx(Button, {
          "data-testid": "generate-api-key-button",
          onClick: () => handleGenerateAPIKey(keys, refresh),
          children: "Generate API key"
        }) : null,
        "data-testid": "api-keys-page",
        children: keys.length > 0 ? /*#__PURE__*/_jsx(TableList.Provider, {
          columnDefs: apiKeysColumnDefs,
          hasActionColumn: true,
          sort: {
            sort: sort.direction,
            colIndex: apiKeysHeaderItems.findIndex(item => item.id === sort.field)
          },
          onSortChange: handleSortChange,
          children: /*#__PURE__*/_jsx(TableList.Structure, {
            header: /*#__PURE__*/_jsx(TableList.Header, {
              items: apiKeysHeaderItems,
              "data-testid": "api-keys-table-header"
            }),
            content: keys.map((key, index) => /*#__PURE__*/_jsx(TableList.Row, {
              items: [{
                value: key.name,
                'data-testid': "api-key-name-".concat(index)
              }, {
                value: formatAPIKeyValue(key.id),
                'data-testid': "api-key-id-".concat(index)
              }, {
                value: formatShortMonthUSDate(new Date(key.createdOn)),
                'data-testid': "api-key-created-".concat(index)
              }, {
                value: formatShortMonthUSDate(new Date(key.updatedOn)),
                'data-testid': "api-key-updated-".concat(index)
              }],
              actionColumn: /*#__PURE__*/_jsxs(TableListActions, {
                testId: "api-key-actions-".concat(index),
                children: [/*#__PURE__*/_jsx(DropdownMenu.Item, {
                  iconStart: /*#__PURE__*/_jsx(RefreshIcon, {}),
                  onClick: () => {
                    overlayContext.showModal({
                      content: _ref3 => {
                        let {
                          dismissModal
                        } = _ref3;
                        return /*#__PURE__*/_jsx(RegenerateAPIKeyModal, {
                          apiKey: keys[index],
                          dismissModal: dismissModal,
                          refresh: refresh
                        });
                      },
                      closeOnClickOutside: true,
                      closeOnEscapeKey: true
                    });
                  },
                  "data-testid": "regenerate-api-key-".concat(index),
                  children: "Regenerate API key"
                }), /*#__PURE__*/_jsx(DropdownMenu.Item, {
                  iconStart: /*#__PURE__*/_jsx(Edit2Liner24, {}),
                  onClick: () => {
                    overlayContext.showModal({
                      content: _ref4 => {
                        let {
                          dismissModal
                        } = _ref4;
                        return /*#__PURE__*/_jsx(RenameAPIKeyModal, {
                          apiKey: keys[index],
                          getIsNameValid: name => _getIsNameValid(name, keys, keys[index].name),
                          dismissModal: dismissModal,
                          refresh: refresh
                        });
                      },
                      closeOnClickOutside: true,
                      closeOnEscapeKey: true
                    });
                  },
                  "data-testid": "rename-api-key-".concat(index),
                  children: "Rename"
                }), /*#__PURE__*/_jsx(DropdownMenu.Item, {
                  skin: "error",
                  iconStart: /*#__PURE__*/_jsx(TrashLiner24, {}),
                  onClick: () => {
                    overlayContext.showModal({
                      content: _ref5 => {
                        let {
                          dismissModal
                        } = _ref5;
                        return /*#__PURE__*/_jsx(DeactivateAPIKeyModal, {
                          apiKey: keys[index],
                          dismissModal: dismissModal,
                          refresh: refresh
                        });
                      },
                      closeOnClickOutside: true,
                      closeOnEscapeKey: true
                    });
                  },
                  "data-testid": "deactivate-api-key-".concat(index),
                  children: "Deactivate API key"
                })]
              })
            }, index))
          })
        }) : /*#__PURE__*/_jsx(APIKeysEmptyState, {
          generateKey: () => handleGenerateAPIKey(keys, refresh)
        })
      });
    }
  }, "".concat(sort.field, "-").concat(sort.direction));
};
export default APIKeysPage;